/* R2'ye doğrudan yükleme için imzalı PUT adresleri (plan §5.2, adım 6-7).

   Neden Worker'ın R2 bağlaması yetmiyor: bağlama yalnızca Worker'ın kendi
   içinden okuyup yazar, dışarıya verilebilecek bir adres üretemez. Dosyayı
   Worker üzerinden geçirmek ise 100 MB istek gövdesi sınırına takılır —
   2 GB'lık bir çekim yüklemesi için baştan ölü bir yol. Bu yüzden tarayıcı
   doğrudan R2'ye yazar; Worker yalnızca kısa ömürlü izni imzalar.

   R2 bu iş için S3 uyumlu ucu kullanır, yani AWS Signature Version 4.
   Bağlama değil, ayrı bir R2 API jetonu (access key id + secret) gerekir.

   signing.ts gibi yalnızca Web Crypto kullanır — Workers'ta da Node'da da
   aynı çalışır, bağımlılık eklemez. */

const enc = new TextEncoder();

export type S3Credentials = {
  accessKeyId: string;
  secretAccessKey: string;
};

export type PresignInput = {
  /** İmzalanacak tam adres (sorgu dizesi olmadan). */
  url: string;
  method: 'PUT' | 'GET' | 'HEAD' | 'DELETE';
  credentials: S3Credentials;
  /** Adresin ömrü, saniye. S3 üst sınırı 604800 (7 gün). */
  expiresIn: number;
  /** R2 için 'auto'; AWS test vektörleri için gerçek bölge. */
  region: string;
  service?: string;
  /** Testler zamanı sabitleyebilsin diye enjekte edilebilir. */
  now?: Date;
};

const hex = (buf: ArrayBuffer): string =>
  [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');

async function sha256Hex(s: string): Promise<string> {
  return hex(await crypto.subtle.digest('SHA-256', bytes(s)));
}

/* Taze bir ArrayBuffer. TextEncoder'ın döndürdüğü görünümü doğrudan
   dolaştırmak ArrayBuffer/SharedArrayBuffer değişkenliğine takılıyor;
   kopyalamak hem tipi sabitler hem de bu boyutlarda ölçülemez. */
function bytes(s: string): ArrayBuffer {
  const view = enc.encode(s);
  const buf = new ArrayBuffer(view.byteLength);
  new Uint8Array(buf).set(view);
  return buf;
}

async function hmac(key: ArrayBuffer, data: string): Promise<ArrayBuffer> {
  const k = await crypto.subtle.importKey('raw', key, { name: 'HMAC', hash: 'SHA-256' }, false, [
    'sign'
  ]);
  return crypto.subtle.sign('HMAC', k, bytes(data));
}

/* RFC 3986. encodeURIComponent !'()* karakterlerini bırakır, S3 bırakmaz —
   bu fark imzayı sessizce bozar, o yüzden tek tek kaçırılır. */
function uriEncode(s: string): string {
  return encodeURIComponent(s).replace(
    /[!'()*]/g,
    (c) => '%' + c.charCodeAt(0).toString(16).toUpperCase()
  );
}

/** Yol: her parça ayrı kaçırılır, '/' korunur. */
function canonicalPath(pathname: string): string {
  return pathname
    .split('/')
    .map((seg) => uriEncode(decodeURIComponent(seg)))
    .join('/');
}

/** Sorgu: anahtar bayt sırasına göre sıralanır, sonra kaçırılır. */
function canonicalQuery(params: [string, string][]): string {
  return [...params]
    .sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : a[1] < b[1] ? -1 : 1))
    .map(([k, v]) => `${uriEncode(k)}=${uriEncode(v)}`)
    .join('&');
}

/** `20130524T000000Z` ve `20130524`. */
function stamps(now: Date): { amzDate: string; dateStamp: string } {
  const amzDate = now.toISOString().replace(/[:-]/g, '').replace(/\.\d{3}/, '');
  return { amzDate, dateStamp: amzDate.slice(0, 8) };
}

/**
 * Sorgu dizesinde taşınan SigV4 imzası üretir (presigned URL).
 *
 * Gövde imzalanmaz (`UNSIGNED-PAYLOAD`): tarayıcı dosyayı akıtarak
 * gönderiyor, imza anında içeriğin karması bilinmiyor.
 */
export async function presign(input: PresignInput): Promise<string> {
  const { url, method, credentials, expiresIn, region } = input;
  const service = input.service ?? 's3';
  const now = input.now ?? new Date();

  if (expiresIn < 1 || expiresIn > 604800) {
    throw new Error(`expiresIn 1..604800 aralığında olmalı, verilen: ${expiresIn}`);
  }

  const u = new URL(url);
  const { amzDate, dateStamp } = stamps(now);
  const scope = `${dateStamp}/${region}/${service}/aws4_request`;

  const query: [string, string][] = [
    ...[...u.searchParams.entries()].map(([k, v]) => [k, v] as [string, string]),
    ['X-Amz-Algorithm', 'AWS4-HMAC-SHA256'],
    ['X-Amz-Credential', `${credentials.accessKeyId}/${scope}`],
    ['X-Amz-Date', amzDate],
    ['X-Amz-Expires', String(expiresIn)],
    ['X-Amz-SignedHeaders', 'host']
  ];

  const canonicalRequest = [
    method,
    canonicalPath(u.pathname),
    canonicalQuery(query),
    `host:${u.host}\n`,
    'host',
    'UNSIGNED-PAYLOAD'
  ].join('\n');

  const stringToSign = [
    'AWS4-HMAC-SHA256',
    amzDate,
    scope,
    await sha256Hex(canonicalRequest)
  ].join('\n');

  /* İmzalama anahtarı zincirlemeli türetilir: tarih → bölge → servis →
     sonlandırıcı. Sızan bir gün-anahtarı yalnızca o günü açar. */
  const kDate = await hmac(bytes(`AWS4${credentials.secretAccessKey}`), dateStamp);
  const kRegion = await hmac(kDate, region);
  const kService = await hmac(kRegion, service);
  const kSigning = await hmac(kService, 'aws4_request');
  const signature = hex(await hmac(kSigning, stringToSign));

  return `${u.origin}${canonicalPath(u.pathname)}?${canonicalQuery(query)}&X-Amz-Signature=${signature}`;
}

/** R2'nin S3 ucu — yol biçimli: `<hesap>.r2.cloudflarestorage.com/<kova>/<anahtar>`. */
export function r2Endpoint(accountId: string, bucket: string, key: string): string {
  const path = key.split('/').map(encodeURIComponent).join('/');
  return `https://${accountId}.r2.cloudflarestorage.com/${bucket}/${path}`;
}

export type R2PresignInput = {
  accountId: string;
  bucket: string;
  key: string;
  credentials: S3Credentials;
  expiresIn?: number;
  now?: Date;
};

/** Tek karenin tek katmanı için imzalı PUT adresi. */
export function presignR2Put(input: R2PresignInput): Promise<string> {
  return presign({
    url: r2Endpoint(input.accountId, input.bucket, input.key),
    method: 'PUT',
    credentials: input.credentials,
    /* Varsayılan 15 dakika: yavaş bir bağlantıda 700 KB'lık bir kare için
       fazlasıyla yeterli, sızan bir adres için kısa. */
    expiresIn: input.expiresIn ?? 900,
    region: 'auto',
    ...(input.now ? { now: input.now } : {})
  });
}
